import { LitElement, html, nothing, type PropertyValues } from 'lit'
import { customElement, property, state, query } from 'lit/decorators.js'
import type { CSCSegmentedControlChangeEvent } from '@cloudsuite/mosaic-elements'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { style } from './product-tile-configurator.style.js'
import type { CombinedLayoutConfig, ImageDisplayConfig, SimpleLayoutCfg } from 'components/shared/base-product-tile/base-product-tile-types.js'
import { CONFIG_MODES, DEFAULT_TILE_VIEW, MAX_COLUMNS_PER_ROW, MAX_ROWS, MOBILE_CONFIG_MODES, TILE_VIEWS, editModeForView, isMobileConfigMode, isPreviewOnlyView, isSectionFull, layoutKindFor, mobileModeFor, viewTitle, type AreaLayout, type ConfigMode, type LayoutKind, type MobileConfigMode, type Section, type TileView, type Widget } from './editor-state.js'
import { isCombinedLayoutConfig } from 'components/shared/base-product-tile/layout-config.js'
import { copyWidgets as copyWidgetsBetweenViews, countWidgets } from './copy-widgets.js'
import { replaceDeprecatedWidget } from './deprecated-widgets.js'
import { DEFAULT_PROPERTIES, DISPLAY_NAMES } from './widget-metadata.js'
import { generateSimpleLayoutCfg, applySimpleLayoutCfg } from './layout-mapper.js'
import { createDefaultAreaLayout, createDefaultAreaLayoutByMode, createDefaultSections, createDefaultSectionsByMode, createDefaultZoneSection, createListDefaultSections, defaultSectionsFor } from './default-sections.js'
import { displayConfigForMode, imageConfigFromBranches, imageConfigFromFlatDisplay } from './image-config-mapper.js'
import { provide } from '@lit/context'
import {
  AreaLayoutContext,
  AreaPaddingContext,
  CanvasStateContext,
  CustomizedMobileModesContext,
  ImageConfigContext,
  ImageFocusContext,
  MobileModeContext,
  SelectedAreaContext,
  SelectedWidgetContext,
  TileConfiguratorActionsContext,
  type AreaLayoutContextType,
  type AreaPaddingContextType,
  type CustomizedMobileModesContextType,
  type DynamicArea,
  type DynamicAreaSettings,
  type ImageConfigContextType,
  type ImageFlowSlot,
  type ImageFocusContextType,
  type MobileModeContextType,
  type SelectedAreaContextType,
  type SelectedWidgetContextType,
  type SelectedZoneRef,
  type TileConfiguratorActionsContextType,
} from './context.js'
import { productTileProductContext } from 'components/discovery/product/tile-parts/tile-product-context.js'
import { ProductModel, type InternalProduct } from 'components/discovery/product/tile-parts/tile-parts-typing.js'
import { tilePartProductPresets, type TilePartProductPreset } from 'components/discovery/product/tile-parts/parts/tile-part-story-utils.js'
import './components/tile-configurator-widget-bar.js'
import './components/tile-configurator-zone-panel.js'
import './components/tile-configurator-import-export-modal.js'
import type { CSCTileConfiguratorImportExportModal } from './components/tile-configurator-import-export-modal.js'
import './components/tile-configurator-copy-modal.js'
import type { CSCTileConfiguratorCopyModal, WidgetsCopiedDetail } from './components/tile-configurator-copy-modal.js'
import './components/tile-configurator-sidebar.js'
import './components/tile-configurator-canvas.js'

/**
 * The 3 mock "types" of product data the configurator's live canvas can preview with — the exact
 * same default/withVariants/withConfigurator data the individual tile-part stories use
 * (`tile-part-story-utils.ts`). This used to be a separate, hand-maintained copy with its own
 * richer shape (id/levies/a singular variant) — that duplication drifted out of sync more than
 * once (e.g. featured_attributes missing display_mode entirely), so tilePartProductPresets is
 * now the single source of truth and already carries those fields too. Don't reintroduce a
 * separate copy here — extend tilePartProductPresets instead if the configurator ever needs a
 * field the tile-part demos don't.
 */
export type PreviewProductPreset = TilePartProductPreset
export const previewProductPresets: Record<PreviewProductPreset, any> = tilePartProductPresets

const MODE_ICONS: Record<LayoutKind, string> = { grid: 'grid-2', list: 'list' }

const VIEW_TOOLTIP_DELAY_MS = 200
const VIEW_TOOLTIP_HIDE_DELAY_MS = 100
const TOAST_HIDE_DELAY_MS = 6000

const plural = (count: number, word: string): string => `${count} ${word}${count === 1 ? '' : 's'}`

interface ConfiguratorToast {
  message: string
  view?: TileView
}

export const createDefaultPreviewProduct = (): any => (new ProductModel() as any).with(previewProductPresets.withVariants)

@customElement('csc-product-tile-configurator')
export class CSCProductTileConfigurator extends LitElement {
  static override styles = [hostStyle, style]

  @property({ type: Object, attribute: 'preview-product' })
  previewProduct: any = createDefaultPreviewProduct()

  @provide({ context: CanvasStateContext })
  @state()
  private sectionsByMode: Record<ConfigMode, Section[]> = createDefaultSectionsByMode()

  @provide({ context: AreaLayoutContext })
  @state()
  private areaLayoutByMode: AreaLayoutContextType = createDefaultAreaLayoutByMode()

  @provide({ context: SelectedWidgetContext })
  @state()
  selectedZone: SelectedWidgetContextType = null

  @provide({ context: SelectedAreaContext })
  @state()
  selectedArea: SelectedAreaContextType = null

  @provide({ context: ImageFocusContext })
  @state()
  imageFocused: ImageFocusContextType = null

  // View-only preview toggle — see MobileModeContextType. Doesn't touch tile-config at all;
  // switching it on just re-renders the same grid/list sections the way mobile really looks,
  // and clears any active selection since the canvas becomes fully non-interactive while it's on.
  @provide({ context: MobileModeContext })
  @state()
  mobileMode: MobileModeContextType = false

  @provide({ context: ImageConfigContext })
  private imageConfig: ImageConfigContextType = {}

  @provide({ context: AreaPaddingContext })
  @state()
  private areaPaddingByMode: AreaPaddingContextType = { grid: {}, list: {}, gridMobile: {}, listMobile: {} }

  @provide({ context: CustomizedMobileModesContext })
  @state()
  private customizedMobileModes: CustomizedMobileModesContextType = []

  @provide({ context: TileConfiguratorActionsContext })
  private actions!: TileConfiguratorActionsContextType

  // The left rail's Widgets tab toggles the widget library panel; its ✕ closes it too.
  @state()
  private widgetPanelOpen = true

  // Which canvas is visible — both stay rendered (only the inactive one is hidden), so each
  // keeps its own DOM/state across switches; this is purely which one the author is looking at.
  @state()
  private activeViewValue = DEFAULT_TILE_VIEW.value

  @state()
  private confirmingMobileReset = false

  // True only while the canvas's preview button is hovered — see CSCTileConfiguratorCanvas.cleanPreview.
  @state()
  private cleanPreview = false

  // cse-segmented-control clips its own overflow (overflow-x:auto in its shadow DOM, no hook to
  // change it), so a tooltip rendered inside an item gets cut off with a scrollbar. Instead one
  // shared cse-tooltip sits outside the control, overlaid on whichever item is hovered/focused.
  @state()
  private viewTooltip: { label: string; left: number; width: number } | null = null

  private viewTooltipTimer: ReturnType<typeof setTimeout> | null = null

  @state()
  private viewBadgeOffsets: number[] = []

  private viewToggleObserver: ResizeObserver | null = null

  @state()
  private toast: ConfiguratorToast | null = null

  private toastTimer: ReturnType<typeof setTimeout> | null = null

  private widgetIdCounter = 0
  private zoneIdCounter = 1

  private sectionsFor(mode: ConfigMode): Section[] {
    return this.sectionsByMode[mode]
  }

  private updateSections(mode: ConfigMode, sections: Section[]) {
    this.sectionsByMode = { ...this.sectionsByMode, [mode]: sections }
  }

  private areaLayoutFor(mode: ConfigMode): AreaLayout {
    return this.areaLayoutByMode[mode]
  }

  private updateAreaLayout(mode: ConfigMode, areaLayout: AreaLayout) {
    this.areaLayoutByMode = { ...this.areaLayoutByMode, [mode]: areaLayout }
  }

  private dropAreaTypeFor(area: DynamicArea): 'product-info' | 'product-footer' {
    return area === 'productInfo' ? 'product-info' : 'product-footer'
  }

  private idPrefixFor(area: DynamicArea): string {
    return area === 'productInfo' ? 'product-info' : 'order-actions'
  }

  private nextZoneId(mode: ConfigMode, area: DynamicArea): string {
    const takenIds = new Set(this.sectionsFor(mode).map(section => section.id))
    let zoneId = `${this.idPrefixFor(area)}-zone-${++this.zoneIdCounter}`
    while (takenIds.has(zoneId)) zoneId = `${this.idPrefixFor(area)}-zone-${++this.zoneIdCounter}`
    return zoneId
  }

  override connectedCallback(): void {
    super.connectedCallback()

    if (!this.actions) {
      this.actions = {
        placeWidget: (mode, sectionId, type, properties) => {
          const sections = this.sectionsFor(mode)
          const section = sections.find(s => s.id === sectionId)
          if (!section || !this.canPlaceWidget(mode, sectionId)) return
          const placedWidget: Widget = { ...this.createWidget(type, properties), section: sectionId, sequence: section.widgets.length + 1 }
          this.updateSections(mode, sections.map(s => s.id === sectionId ? { ...s, widgets: [...s.widgets, placedWidget] } : s))
        },
        moveWidget: (sourceMode, targetMode, widgetId, targetSectionId) => {
          const sourceSections = this.sectionsFor(sourceMode)
          const sourceSection = sourceSections.find(s => s.widgets.some(w => w.id === widgetId))
          const widget = sourceSection?.widgets.find(w => w.id === widgetId)
          if (!sourceSection || !widget) return
          if (sourceMode === targetMode && sourceSection.id === targetSectionId) return
          if (!this.canPlaceWidget(targetMode, targetSectionId)) return

          const movedWidget: Widget = { ...widget, section: targetSectionId }

          if (sourceMode === targetMode) {
            this.updateSections(sourceMode, sourceSections.map(s => {
              if (s.id === sourceSection.id) {
                return { ...s, widgets: s.widgets.filter(w => w.id !== widgetId) }
              }
              if (s.id === targetSectionId) {
                return { ...s, widgets: [...s.widgets, { ...movedWidget, sequence: s.widgets.length + 1 }] }
              }
              return s
            }))
          } else {
            this.updateSections(sourceMode, sourceSections.map(s => s.id === sourceSection.id ? { ...s, widgets: s.widgets.filter(w => w.id !== widgetId) } : s))
            const targetSections = this.sectionsFor(targetMode)
            this.updateSections(targetMode, targetSections.map(s => s.id === targetSectionId ? { ...s, widgets: [...s.widgets, { ...movedWidget, sequence: s.widgets.length + 1 }] } : s))
          }

          if (this.selectedZone?.mode === sourceMode && this.selectedZone?.widgetId === widgetId) {
            this.selectedZone = { mode: targetMode, sectionId: targetSectionId, widgetId }
          }
        },
        swapWidgets: (mode, widgetIdA, widgetIdB) => {
          const sections = this.sectionsFor(mode)
          const section = sections.find(s => s.widgets.some(w => w.id === widgetIdA))
          const widgetA = section?.widgets.find(w => w.id === widgetIdA)
          const widgetB = section?.widgets.find(w => w.id === widgetIdB)
          if (!section || !widgetA || !widgetB) return

          this.updateSections(mode, sections.map(s => s.id === section.id ? {
            ...s,
            widgets: s.widgets.map(w => {
              if (w.id === widgetIdA) return { ...w, sequence: widgetB.sequence }
              if (w.id === widgetIdB) return { ...w, sequence: widgetA.sequence }
              return w
            }),
          } : s))
        },
        removeWidget: (mode, widgetId) => {
          const updatedSections = this.sectionsFor(mode).map(s => ({ ...s, widgets: s.widgets.filter(w => w.id !== widgetId) }))
          this.updateSections(mode, updatedSections)
          const emptySections = updatedSections.filter(s => s.widgets.length === 0)
          const removedSelectedWidget = this.selectedZone?.mode === mode && this.selectedZone.widgetId === widgetId
          if (removedSelectedWidget || (emptySections.length === updatedSections.length && this.selectedZone?.mode === mode)) {
            this.selectedZone = null
          }
        },
        selectWidget: (ref: SelectedZoneRef) => {
          this.selectedZone = ref
          this.selectedArea = null
          this.imageFocused = null
        },
        selectImage: (mode) => {
          this.selectedZone = null
          this.selectedArea = null
          this.imageFocused = mode
        },
        selectArea: (mode, area) => {
          this.selectedZone = null
          this.selectedArea = { mode, area }
          this.imageFocused = null
        },
        updateWidgetProperty: (mode, widgetId, key, value) => {
          this.updateSections(mode, this.sectionsFor(mode).map(s => ({
            ...s,
            widgets: s.widgets.map(w => w.id === widgetId ? { ...w, properties: { ...w.properties, [key]: value } } : w),
          })))
        },
        updateWidgetShowWhen: (mode, widgetId, showWhen) => {
          this.updateSections(mode, this.sectionsFor(mode).map(s => ({
            ...s,
            widgets: s.widgets.map(w => {
              if (w.id !== widgetId) return w
              const updated: Widget = { ...w, showWhen }
              if (!showWhen.length) delete updated.showWhen
              return updated
            }),
          })))
        },
        addRow: (mode, area) => {
          const areaLayout = this.areaLayoutFor(mode)
          const rows = areaLayout[area]
          if (rows.length >= MAX_ROWS) return

          const zoneId = this.nextZoneId(mode, area)
          const newSection = createDefaultZoneSection(zoneId, `Zone ${rows.flat().length + 1}`, this.dropAreaTypeFor(area))
          this.updateSections(mode, [...this.sectionsFor(mode), newSection])
          this.updateAreaLayout(mode, { ...areaLayout, [area]: [...rows, [zoneId]] })
        },
        addColumn: (mode, area, rowIndex) => {
          const areaLayout = this.areaLayoutFor(mode)
          const rows = areaLayout[area]
          const row = rows[rowIndex]
          if (!row || row.length >= MAX_COLUMNS_PER_ROW) return

          const zoneId = this.nextZoneId(mode, area)
          const newSection = createDefaultZoneSection(zoneId, `Zone ${rows.flat().length + 1}`, this.dropAreaTypeFor(area))
          this.updateSections(mode, [...this.sectionsFor(mode), newSection])
          this.updateAreaLayout(mode, {
            ...areaLayout,
            [area]: rows.map((r, i) => i === rowIndex ? [...r, zoneId] : r),
          })
        },
        removeZone: (mode, area, sectionId) => {
          const areaLayout = this.areaLayoutFor(mode)
          const rows = areaLayout[area]
          const nextRows = rows
            .map(row => row.filter(id => id !== sectionId))
            .filter(row => row.length > 0)
          this.updateAreaLayout(mode, { ...areaLayout, [area]: nextRows })
          this.updateSections(mode, this.sectionsFor(mode).filter(s => s.id !== sectionId))

          if (this.selectedZone?.mode === mode && this.selectedZone?.sectionId === sectionId) {
            this.selectedZone = null
          }
        },
        updateZoneStyle: (mode, sectionId, partial) => {
          this.updateSections(mode, this.sectionsFor(mode).map(s => s.id === sectionId ? { ...s, ...partial } : s))
        },
        updateImageConfig: (partial: Partial<ImageConfigContextType>) => {
          this.imageConfig = { ...this.imageConfig, ...partial }
        },
        updateAreaPadding: (mode: ConfigMode, area: DynamicArea | ImageFlowSlot, settings: DynamicAreaSettings) => {
          this.areaPaddingByMode = {
            ...this.areaPaddingByMode,
            [mode]: { ...this.areaPaddingByMode[mode], [area]: settings },
          }
        },
        updateAreaGap: (mode: ConfigMode, area: DynamicArea, settings: DynamicAreaSettings) => {
          this.areaPaddingByMode = {
            ...this.areaPaddingByMode,
            [mode]: { ...this.areaPaddingByMode[mode], [area]: settings },
          }
        },
        updateAreaAlignment: (mode: ConfigMode, area: DynamicArea, settings: DynamicAreaSettings) => {
          this.areaPaddingByMode = {
            ...this.areaPaddingByMode,
            [mode]: { ...this.areaPaddingByMode[mode], [area]: settings },
          }
        },
        clearSelection: () => {
          this.selectedZone = null
          this.selectedArea = null
          this.imageFocused = null
        },
        copyWidgets: (sourceMode, targetMode, options) => {
          if (sourceMode === targetMode) return 0
          if (isMobileConfigMode(targetMode)) this.actions.customizeMobileMode(targetMode)
          const result = copyWidgetsBetweenViews(
            { sections: this.sectionsFor(sourceMode), areaLayout: this.areaLayoutFor(sourceMode) },
            { sections: this.sectionsFor(targetMode), areaLayout: this.areaLayoutFor(targetMode) },
            options,
            {
              widgetId: type => this.nextWidgetId(type),
              zoneId: area => this.nextZoneId(targetMode, area),
            },
          )
          this.updateSections(targetMode, result.view.sections)
          this.updateAreaLayout(targetMode, result.view.areaLayout)
          return result.added
        },
        customizeMobileMode: (mode) => {
          if (this.customizedMobileModes.includes(mode)) return
          const desktopMode = layoutKindFor(mode)
          const sections = structuredClone(this.sectionsFor(desktopMode))
          sections.forEach(section => {
            delete section.hideUntilHover
            section.widgets.forEach(widget => { widget.id = this.nextWidgetId(widget.type) })
          })
          this.updateSections(mode, sections)
          this.updateAreaLayout(mode, structuredClone(this.areaLayoutFor(desktopMode)))
          this.areaPaddingByMode = { ...this.areaPaddingByMode, [mode]: structuredClone(this.areaPaddingByMode[desktopMode]) }
          const desktopImageView = this.imageConfig[desktopMode]
          this.imageConfig = { ...this.imageConfig, ...(desktopImageView && { [mode]: structuredClone(desktopImageView) }) }
          this.customizedMobileModes = MOBILE_CONFIG_MODES.filter(m => m === mode || this.customizedMobileModes.includes(m))
        },
        resetMobileMode: (mode) => {
          this.updateSections(mode, defaultSectionsFor(mode))
          this.updateAreaLayout(mode, createDefaultAreaLayout())
          this.areaPaddingByMode = { ...this.areaPaddingByMode, [mode]: {} }
          this.imageConfig = Object.fromEntries(Object.entries(this.imageConfig).filter(([key]) => key !== mode)) as ImageConfigContextType
          this.customizedMobileModes = this.customizedMobileModes.filter(m => m !== mode)
          if (this.selectedZone?.mode === mode) this.selectedZone = null
          if (this.selectedArea?.mode === mode) this.selectedArea = null
          if (this.imageFocused === mode) this.imageFocused = null
        },
        loadFromCombinedConfig: (parsed: unknown) => {
          if (!parsed || typeof parsed !== 'object') {
            throw new Error('Invalid JSON structure')
          }

          const replacedCounts = new Map<Widget['type'], number>()
          const createWidgetFn = (type: Widget['type'], properties?: Record<string, any>) => {
            const replaced = replaceDeprecatedWidget(type, properties)
            if (replaced.type !== type) replacedCounts.set(replaced.type, (replacedCounts.get(replaced.type) ?? 0) + 1)
            return this.createWidget(replaced.type, replaced.properties)
          }

          if (isCombinedLayoutConfig(parsed)) {
            const nextSections = { ...this.sectionsByMode }
            const nextAreaLayout = { ...this.areaLayoutByMode }
            const nextAreaPadding = { ...this.areaPaddingByMode }
            const customized: MobileConfigMode[] = []
            CONFIG_MODES.forEach(mode => {
              const branch = parsed[mode]
              if (branch && this.isValidSimpleLayoutCfg(branch)) {
                const applied = applySimpleLayoutCfg(branch, defaultSectionsFor(mode), createWidgetFn)
                nextSections[mode] = applied.sections
                nextAreaLayout[mode] = applied.areaLayout
                nextAreaPadding[mode] = applied.areaPadding
                if (isMobileConfigMode(mode)) customized.push(mode)
              } else if (isMobileConfigMode(mode)) {
                nextSections[mode] = defaultSectionsFor(mode)
                nextAreaLayout[mode] = createDefaultAreaLayout()
                nextAreaPadding[mode] = {}
              }
            })
            this.sectionsByMode = nextSections
            this.areaLayoutByMode = nextAreaLayout
            this.areaPaddingByMode = nextAreaPadding
            this.customizedMobileModes = customized
            this.imageConfig = imageConfigFromBranches(Object.fromEntries(CONFIG_MODES.map(mode => [mode, parsed[mode]?.image])))
          } else if (this.isValidSimpleLayoutCfg(parsed)) {
            const config = parsed
            const appliedGrid = applySimpleLayoutCfg(config, createDefaultSections(), createWidgetFn)
            const appliedList = applySimpleLayoutCfg(config, createListDefaultSections(), createWidgetFn)
            this.sectionsByMode = { ...createDefaultSectionsByMode(), grid: appliedGrid.sections, list: appliedList.sections }
            this.areaLayoutByMode = { ...createDefaultAreaLayoutByMode(), grid: appliedGrid.areaLayout, list: appliedList.areaLayout }
            this.areaPaddingByMode = { grid: appliedGrid.areaPadding, list: appliedList.areaPadding, gridMobile: {}, listMobile: {} }
            this.customizedMobileModes = []
            this.imageConfig = imageConfigFromFlatDisplay(config.image)
          } else {
            throw new Error('Config must contain at least one of: image, productInfo, or orderActions (or grid/list/gridMobile/listMobile keys)')
          }

          this.selectedZone = null
          this.selectedArea = null
          this.imageFocused = null
          if (replacedCounts.size) {
            const message = [...replacedCounts].map(([type, count]) => `Replaced ${plural(count, 'deprecated widget')} with ${DISPLAY_NAMES[type]}`).join('. ')
            this.showToast({ message })
          }
        },
        exportCombinedConfig: () => this.generateCombinedConfiguration(),
      }
    }
  }

  @query('csc-tile-configurator-import-export-modal')
  private importExportModal!: CSCTileConfiguratorImportExportModal

  @query('csc-tile-configurator-copy-modal')
  private copyModal!: CSCTileConfiguratorCopyModal

  @provide({ context: productTileProductContext })
  @state()
  parsedPreviewProduct: InternalProduct = this.parsePreviewProduct(this.previewProduct)

  private parsePreviewProduct(value: unknown): InternalProduct {
    if (value) {
      const rawProduct = typeof value === 'string' ? JSON.parse(value) : value as Record<string, unknown>
      return new ProductModel().with(rawProduct).parseToInternal()
    }
    return new ProductModel().parseToInternal()
  }

  override willUpdate(changedProperties: PropertyValues) {
    if (changedProperties.has('previewProduct')) {
      this.parsedPreviewProduct = this.parsePreviewProduct(this.previewProduct)
    }
    if (changedProperties.has('activeViewValue') || changedProperties.has('customizedMobileModes')) {
      this.mobileMode = isPreviewOnlyView(this.activeView, this.customizedMobileModes)
      this.confirmingMobileReset = false
    }
  }

  private nextWidgetId(type: Widget['type']): string {
    return `${type}-${Date.now()}-${this.widgetIdCounter++}`
  }

  private createWidget(type: Widget['type'], properties?: Record<string, any>): Widget {
    return {
      id: this.nextWidgetId(type),
      type,
      section: '',
      sequence: 0,
      position: 'center',
      properties: { ...this.getDefaultProperties(type), ...properties },
    }
  }

  private getDefaultProperties(type: Widget['type']): Record<string, any> {
    return DEFAULT_PROPERTIES[type] || {}
  }

  // Smart widget placement with collision detection
  private canPlaceWidget(mode: ConfigMode, sectionId: string): boolean {
    const section = this.sectionsFor(mode).find(s => s.id === sectionId)
    if (!section) return false

    return !isSectionFull(section)
  }

  private withImageConfig(cfg: SimpleLayoutCfg, displayConfig: ImageDisplayConfig): SimpleLayoutCfg {
    return { ...cfg, image: { ...cfg.image, ...displayConfig } }
  }

  private generateBranch(mode: ConfigMode): SimpleLayoutCfg {
    return this.withImageConfig(
      generateSimpleLayoutCfg(this.sectionsByMode[mode], this.areaLayoutByMode[mode], this.areaPaddingByMode[mode]),
      displayConfigForMode(this.imageConfig, mode),
    )
  }

  private generateCombinedConfiguration(): CombinedLayoutConfig {
    const config: CombinedLayoutConfig = { grid: this.generateBranch('grid'), list: this.generateBranch('list') }
    MOBILE_CONFIG_MODES
      .filter(mode => this.customizedMobileModes.includes(mode))
      .forEach(mode => {
        const branch = this.generateBranch(mode)
        if (JSON.stringify(branch) !== JSON.stringify(config[layoutKindFor(mode)])) config[mode] = branch
      })
    return config
  }

  private isValidSimpleLayoutCfg(config: unknown): config is SimpleLayoutCfg {
    if (!config || typeof config !== 'object') return false
    const c = config as Record<string, unknown>
    return !!(c['image'] || c['productInfo'] || c['orderActions'])
  }

  private async handleOpenImportModal() {
    await this.updateComplete
    await this.importExportModal?.openImport()
  }

  private async handleOpenExportModal() {
    await this.updateComplete
    await this.importExportModal?.openExport()
  }

  // One toggle for both view dimensions: which canvas (grid/list) and whether it's previewed as
  // mobile. Switching layout clears the selection (it would point into the now-hidden canvas),
  // and so does turning mobile preview on (the canvas becomes fully inert — see MobileModeContextType).
  // Same 200ms show delay as cse-tooltip's own hover handling. Hiding is delayed briefly too, so
  // moving straight to a neighbouring option (mouseleave fires before the next mouseenter) finds
  // the tooltip still open and switches it immediately instead of waiting all over again.
  private showViewTooltip(event: Event, label: string) {
    const item = event.currentTarget as HTMLElement
    const container = item.closest('.view-toggle') ?? this.renderRoot.querySelector('.view-toggle')
    if (!container) return
    const itemRect = item.getBoundingClientRect()
    const containerRect = container.getBoundingClientRect()
    const next = { label, left: itemRect.left - containerRect.left, width: itemRect.width }
    this.clearViewTooltipTimer()
    if (this.viewTooltip) {
      this.viewTooltip = next
      return
    }
    this.viewTooltipTimer = setTimeout(() => {
      this.viewTooltipTimer = null
      this.viewTooltip = next
    }, VIEW_TOOLTIP_DELAY_MS)
  }

  private hideViewTooltip = () => {
    this.clearViewTooltipTimer()
    if (!this.viewTooltip) return
    this.viewTooltipTimer = setTimeout(() => {
      this.viewTooltipTimer = null
      this.viewTooltip = null
    }, VIEW_TOOLTIP_HIDE_DELAY_MS)
  }

  private clearViewTooltipTimer() {
    if (this.viewTooltipTimer === null) return
    clearTimeout(this.viewTooltipTimer)
    this.viewTooltipTimer = null
  }

  override firstUpdated() {
    const viewToggle = this.renderRoot.querySelector('.view-toggle')
    if (!viewToggle) return
    this.viewToggleObserver = new ResizeObserver(() => this.measureViewBadges())
    this.viewToggleObserver.observe(viewToggle)
    requestAnimationFrame(() => this.measureViewBadges())
  }

  private measureViewBadges() {
    const container = this.renderRoot.querySelector('.view-toggle')
    if (!container) return
    const containerLeft = container.getBoundingClientRect().left
    const offsets = [...container.querySelectorAll('cse-segmented-control-item')]
      .map(item => Math.round(item.getBoundingClientRect().right - containerLeft))
    if (offsets.join() !== this.viewBadgeOffsets.join()) this.viewBadgeOffsets = offsets
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    this.viewToggleObserver?.disconnect()
    this.viewToggleObserver = null
    this.clearViewTooltipTimer()
    this.clearToastTimer()
  }

  private get activeView(): TileView {
    return TILE_VIEWS.find(view => view.value === this.activeViewValue) ?? DEFAULT_TILE_VIEW
  }

  private get activeMode(): ConfigMode {
    return editModeForView(this.activeView, this.customizedMobileModes)
  }

  private async handleOpenCopyModal() {
    await this.updateComplete
    await this.copyModal?.open(this.activeView)
  }

  private handleWidgetsCopied(event: CustomEvent<WidgetsCopiedDetail>) {
    const { view, added } = event.detail
    this.showToast({ message: `Added ${plural(added, 'widget')} to ${viewTitle(view)}`, view })
  }

  private showToast(toast: ConfiguratorToast) {
    this.clearToastTimer()
    this.toast = toast
    this.toastTimer = setTimeout(() => this.dismissToast(), TOAST_HIDE_DELAY_MS)
  }

  private dismissToast() {
    this.clearToastTimer()
    this.toast = null
  }

  private clearToastTimer() {
    if (this.toastTimer === null) return
    clearTimeout(this.toastTimer)
    this.toastTimer = null
  }

  private goToView(view: TileView) {
    this.dismissToast()
    const viewToggle = this.renderRoot.querySelector<HTMLElement & { activateControlItem: (index: number) => void }>('#view-toggle')
    viewToggle?.activateControlItem(TILE_VIEWS.indexOf(view))
  }

  private placedCountsFor(mode: ConfigMode): Partial<Record<Widget['type'], number>> {
    const counts: Partial<Record<Widget['type'], number>> = {}
    this.sectionsByMode[mode].forEach(section => section.widgets.forEach(widget => {
      counts[widget.type] = (counts[widget.type] ?? 0) + 1
    }))
    return counts
  }

  private handleViewChange(event: CSCSegmentedControlChangeEvent) {
    const option = TILE_VIEWS.find(o => o.value === event.value)
    if (!option) return
    const nextMode = editModeForView(option, this.customizedMobileModes)
    if (nextMode !== this.activeMode || isPreviewOnlyView(option, this.customizedMobileModes)) this.actions.clearSelection()
    this.activeViewValue = option.value
  }

  private renderCanvasBlock(mode: ConfigMode) {
    return html`
      <div class="canvas-block">
        <csc-tile-configurator-canvas mode=${mode} ?clean-preview=${this.cleanPreview}></csc-tile-configurator-canvas>
      </div>
    `
  }

  private renderMobileNote() {
    if (!this.activeView.mobile) return nothing
    const mobileMode = mobileModeFor(this.activeView.mode)
    const stop = (event: Event) => event.stopPropagation()

    if (this.mobileMode) {
      return html`
        <div class="mobile-preview-note-row">
          <div class="mobile-preview-note" role="status" @click=${stop}>
            <cse-icon icon-name="eye" icon-style="regular"></cse-icon>
            <cse-text variant="caption-m">Uses the desktop layout</cse-text>
            <button type="button" class="mobile-note-action customize-mobile-button" @click=${() => this.actions.customizeMobileMode(mobileMode)}>
              Customize for mobile
            </button>
          </div>
        </div>
      `
    }

    return html`
      <div class="mobile-preview-note-row">
        <div class="mobile-preview-note customized" role="status" @click=${stop}>
          <cse-icon icon-name="mobile" icon-style="regular"></cse-icon>
          ${this.confirmingMobileReset ? html`
            <cse-text variant="caption-m">Remove the mobile layout? Changes made here will be lost.</cse-text>
            <button type="button" class="mobile-note-action confirm-reset-mobile-button" @click=${() => this.actions.resetMobileMode(mobileMode)}>Reset</button>
            <button type="button" class="mobile-note-action cancel-reset-mobile-button" @click=${() => { this.confirmingMobileReset = false }}>Cancel</button>
          ` : html`
            <cse-text variant="caption-m">Customized for mobile</cse-text>
            <button type="button" class="mobile-note-action reset-mobile-button" @click=${() => { this.confirmingMobileReset = true }}>Reset to desktop layout</button>
          `}
        </div>
      </div>
    `
  }

  private renderToast({ message, view }: ConfiguratorToast) {
    return html`
      <div class="configurator-toast" role="status">
        <cse-text variant="body-s">${message}</cse-text>
        ${!view || view.value === this.activeViewValue ? nothing : html`
          <button type="button" class="configurator-toast-action" @click=${() => this.goToView(view)}>Go to view</button>
        `}
        <button type="button" class="configurator-toast-close" aria-label="Dismiss" @click=${() => this.dismissToast()}>
          <cse-icon icon-name="xmark" icon-style="solid"></cse-icon>
        </button>
      </div>
    `
  }

  override render() {
    return html`
      <div class="product-tile-configurator-header">
        <cse-text variant="heading-4">Product Tile Configurator</cse-text>

        <div class="view-toggle">
          <cse-segmented-control
            id="view-toggle"
            fit="fit"
            @segmented-control-change=${(e: CSCSegmentedControlChangeEvent) => this.handleViewChange(e)}>
            ${TILE_VIEWS.map(option => html`
              <cse-segmented-control-item
                value=${option.value}
                aria-label=${option.label}
                @mouseenter=${(e: Event) => this.showViewTooltip(e, option.label)}
                @mouseleave=${this.hideViewTooltip}
                @focusin=${(e: Event) => this.showViewTooltip(e, option.label)}
                @focusout=${this.hideViewTooltip}>
                <span class="view-toggle-icons">
                  <cse-icon icon-name=${MODE_ICONS[option.mode]} icon-style="regular"></cse-icon>
                  <cse-icon icon-name=${option.mobile ? 'mobile' : 'desktop'} icon-style="regular"></cse-icon>
                </span>
              </cse-segmented-control-item>
            `)}
          </cse-segmented-control>
          <cse-tooltip
            class="view-toggle-tooltip"
            position="bottom"
            ?open=${!!this.viewTooltip}
            style=${this.viewTooltip ? `left:${this.viewTooltip.left}px;width:${this.viewTooltip.width}px` : ''}>
            ${this.viewTooltip?.label ?? ''}
            <span slot="anchor" class="view-toggle-tooltip-anchor"></span>
          </cse-tooltip>
          ${TILE_VIEWS.map((view, index) => {
            const count = countWidgets(this.sectionsFor(editModeForView(view, this.customizedMobileModes)))
            const offset = this.viewBadgeOffsets[index]
            const inherited = isPreviewOnlyView(view, this.customizedMobileModes)
            return count && offset !== undefined ? html`
              <span class="view-toggle-badge ${inherited ? 'inherited' : ''}" data-view=${view.value} style="left:${offset}px" aria-hidden="true">
                <cse-text variant="caption-s">${count}</cse-text>
              </span>
            ` : nothing
          })}
        </div>

        <div class="header-actions">
          <cse-button
            class="copy-to-button"
            variant="default"
            size="small"
            ?disabled=${this.mobileMode || countWidgets(this.sectionsFor(this.activeMode)) === 0}
            @click=${this.handleOpenCopyModal}>
            <cse-icon icon-name="copy" icon-style="regular"></cse-icon>
            Copy to…
          </cse-button>
          <cse-button variant="default" size="small" @click=${this.handleOpenImportModal}>
            Import JSON
          </cse-button>
          <cse-button variant="primary" size="small" @click=${this.handleOpenExportModal}>
            Export JSON
          </cse-button>
        </div>
      </div>

      <div class="configurator-layout">
        <nav class="left-rail" aria-label="Panels">
          <button
            type="button"
            class="rail-tab ${this.widgetPanelOpen ? 'active' : ''}"
            aria-pressed="${this.widgetPanelOpen ? 'true' : 'false'}"
            aria-controls="widget-panel"
            @click=${() => { this.widgetPanelOpen = !this.widgetPanelOpen }}>
            <cse-icon icon-name="grid-2-plus" icon-style="regular"></cse-icon>
            <cse-text class="rail-tab-label" variant="caption-m">Widgets</cse-text>
          </button>
        </nav>

        <div id="widget-panel" class="left-panel ${this.widgetPanelOpen ? '' : 'panel-collapsed'}" ?inert=${!this.widgetPanelOpen}>
          <div class="left-panel-header">
            <cse-text variant="heading-5">Widgets</cse-text>
            <button
              type="button"
              class="left-panel-close"
              aria-label="Close widgets panel"
              @click=${() => { this.widgetPanelOpen = false }}>
              <cse-icon icon-name="xmark" icon-style="solid"></cse-icon>
            </button>
          </div>
          <csc-tile-configurator-widget-bar .placedCounts=${this.placedCountsFor(this.activeMode)}></csc-tile-configurator-widget-bar>
        </div>

        <div class="canvas-panel-wrapper">
          <div class="canvas-panel" @click=${() => this.actions.clearSelection()}>
            ${this.renderMobileNote()}
            ${CONFIG_MODES
              .filter(mode => !isMobileConfigMode(mode) || this.customizedMobileModes.includes(mode))
              .map(mode => html`
                <div class="canvas-column" ?hidden=${this.activeMode !== mode}>
                  ${this.renderCanvasBlock(mode)}
                </div>
              `)}
          </div>

          <button
            type="button"
            class="clean-preview-button ${this.cleanPreview ? 'active' : ''}"
            aria-label="Hover to preview the tile without editor outlines"
            title="Hover to preview the tile without editor outlines"
            @mouseenter=${() => { this.cleanPreview = true }}
            @mouseleave=${() => { this.cleanPreview = false }}>
            <cse-icon icon-name="eye" icon-style="regular"></cse-icon>
          </button>

          ${this.toast ? this.renderToast(this.toast) : nothing}
        </div>

        <div class="sidebar-panel">
          <csc-tile-configurator-zone-panel></csc-tile-configurator-zone-panel>
          <csc-tile-configurator-sidebar></csc-tile-configurator-sidebar>
        </div>
      </div>

      <csc-tile-configurator-import-export-modal></csc-tile-configurator-import-export-modal>
      <csc-tile-configurator-copy-modal @widgets-copied=${this.handleWidgetsCopied}></csc-tile-configurator-copy-modal>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-product-tile-configurator': CSCProductTileConfigurator
  }
}
